import { createId, SFNodeType } from '@sciflow/schema';
import { setBlockType } from 'prosemirror-commands';
import { Fragment } from 'prosemirror-model';
import { NodeSelection, TextSelection } from 'prosemirror-state';
import {
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  CellSelection,
  deleteColumn,
  deleteRow,
  mergeCells,
  splitCell,
  toggleHeaderColumn,
  toggleHeaderRow,
} from 'prosemirror-tables';
import { insertPoint } from 'prosemirror-transform';
import { insertBlockOutsideList } from './insert-outside-list';

export enum TABLE_COMMANDS {
  'addColumnAfter' = 'addColumnAfter',
  'addColumnBefore' = 'addColumnBefore',
  'deleteColumn' = 'deleteColumn',
  'addRowAfter' = 'addRowAfter',
  'addRowBefore' = 'addRowBefore',
  'deleteRow' = 'deleteRow',
  'mergeCells' = 'mergeCells',
  'splitCell' = 'splitCell',
  'setCellAttr' = 'setCellAttr',
  'toggleHeaderRow' = 'toggleHeaderRow',
  'toggleHeaderColumn' = 'toggleHeaderColumn',
  'toggleHeaderCell' = 'toggleHeaderCell',
  'goToNextCell' = 'goToNextCell',
  'deleteTable' = 'deleteTable',
}

export enum ALIGN_COMMANDS {
  'alignLeft' = 'alignLeft',
  'alignRight' = 'alignRight',
  'alignCenter' = 'alignCenter',
  'alignJustify' = 'alignJustify',
}

export const tableCommands = [
  {
    id: TABLE_COMMANDS.toggleHeaderColumn,
    svgIcon: 'table-column-heading',
    tooltip: 'Toggle header column',
    run: toggleHeaderColumn,
    select: toggleHeaderColumn,
  },
  {
    id: TABLE_COMMANDS.toggleHeaderRow,
    svgIcon: 'table-row-heading',
    tooltip: 'Toggle header row',
    run: toggleHeaderRow,
    select: toggleHeaderRow,
  },
  {
    id: TABLE_COMMANDS.addColumnAfter,
    svgIcon: 'table-new-column-right',
    tooltip: 'Insert column right',
    run: addColumnAfter,
    select: addColumnAfter,
  },
  {
    id: TABLE_COMMANDS.addColumnBefore,
    svgIcon: 'table-new-column-left',
    tooltip: 'Insert column left',
    run: addColumnBefore,
    select: addColumnBefore,
  },
  {
    id: TABLE_COMMANDS.addRowAfter,
    svgIcon: 'table-new-row-below',
    tooltip: 'Add row below',
    run: addRowAfter,
    select: addRowAfter,
  },
  {
    id: TABLE_COMMANDS.addRowBefore,
    svgIcon: 'table-new-row-above',
    tooltip: 'Add row before',
    run: addRowBefore,
    select: addRowBefore,
  },
  {
    id: TABLE_COMMANDS.mergeCells,
    svgIcon: 'table-merge-cells',
    tooltip: 'Merge cells',
    run: mergeCells,
    select: mergeCells,
  },
  {
    id: TABLE_COMMANDS.splitCell,
    svgIcon: 'table-split-cell',
    tooltip: 'Split cell',
    run: splitCell,
    select: splitCell,
  },
  {
    id: TABLE_COMMANDS.deleteColumn,
    svgIcon: 'table-column-delete',
    tooltip: 'Delete column',
    run: deleteColumn,
    select: deleteColumn,
  },
  {
    id: TABLE_COMMANDS.deleteRow,
    svgIcon: 'table-row-delete',
    tooltip: 'Delete row',
    run: deleteRow,
    select: deleteRow,
  },
];

export const alignParagraph = (align: 'justify' | 'left' | 'center' | 'right') => {
  return (state, dispatch) => {

    const parent = state.selection.$anchor.node(-1);
    let inFreePart = parent && parent.type.name === SFNodeType.part && parent.attrs.type === 'free';

    if (!dispatch) {
      if (inFreePart) { return true; }
      if (state.selection instanceof CellSelection) {
        return true;
      }
      if (
        state.selection &&
        state.selection.$anchor.parent &&
        state.selection.$anchor.parent.type.name === 'paragraph'
      ) {
        return true;
      }
    }

    const tr = state.tr;
    if (state.selection instanceof CellSelection) {
      // align all paragraps inside the cell
      for (let range of state.selection.ranges) {
        const from = range.$from.pos;
        const to = range.$to.pos;
        state.doc.descendants((node, pos) => {
          if (node.type.name === 'paragraph' && pos >= from && pos <= to) {
            tr.setNodeMarkup(pos, null, { ...node.attrs, 'text-align': align }, node.marks);
          }
        });
      }
      if (dispatch) {
        dispatch(tr);
      }
      return tr.docChanged;
    } else if (
      state.selection &&
      state.selection.$anchor.parent &&
      state.selection.$anchor.parent.type.name === 'paragraph'
    ) {
      // selection is going through one or more paragraphs
      state.doc.descendants((node, pos) => {
        const to = pos + node.nodeSize;
        if (state.selection.from < pos) {
          return;
        }
        if (state.selection.to > to) {
          return;
        }
        if (node.type.name === 'paragraph') {
          tr.setNodeMarkup(pos, null, { ...node.attrs, 'text-align': align }, node.marks);
        }
      });
      if (dispatch) {
        dispatch(tr);
      }
      return tr.docChanged;
    }
    return false;
  };
};

export const alignCommands = [
  {
    id: ALIGN_COMMANDS.alignLeft,
    svgIcon: 'table-text-align-left',
    tooltip: 'Align text left',
    run: alignParagraph('left'),
    select: alignParagraph('left'),
  },
  {
    id: ALIGN_COMMANDS.alignCenter,
    svgIcon: 'table-text-align-center',
    tooltip: 'Center text',
    run: alignParagraph('center'),
    select: alignParagraph('center'),
  },
  {
    id: ALIGN_COMMANDS.alignRight,
    svgIcon: 'table-text-align-right',
    tooltip: 'Align text right',
    run: alignParagraph('right'),
    select: alignParagraph('right'),
  },
  {
    id: ALIGN_COMMANDS.alignJustify,
    svgIcon: 'table-text-align-justified',
    tooltip: 'Justify text',
    run: alignParagraph('justify'),
    select: alignParagraph('justify'),
  },
];

export const highlight = (searchTerm: string) => (state, dispatch) => {
  const tr = state.tr;
  tr.setMeta('search-and-replace', searchTerm);

  if (dispatch) {
    dispatch(tr);
  }
};

export const insertHyperlink = (
  attrs?: { id?: string; href?: string; title?: string },
  text?: string,
) => {
  return (state, dispatch) => {
    const markType = state.schema.marks.anchor;
    function markActive(state, type) {
      let { from, $from, to, empty } = state.selection;
      if (empty) return type && type.isInSet(state.storedMarks || $from.marks());
      else return state.doc.rangeHasMark(from, to, type);
    }

    function markApplies(doc, ranges, type) {
      for (let i = 0; i < ranges.length; i++) {
        let { $from, $to } = ranges[i];
        let can = $from.depth == 0 ? doc.type.allowsMarkType(type) : false;
        doc.nodesBetween($from.pos, $to.pos, (node) => {
          if (can) return;
          can = node.inlineContent && node.type.allowsMarkType(type);
        });
        if (can) {
          return true;
        }
      }
      return false;
    }

    // if the selection has an active hyperlink mark, return.
    if (!markType || markActive(state, markType)) {
      return false;
    }

    if (attrs == null) {
      attrs = {};
    } // in case the function is called with null a default wouldn't work
    if (!attrs.id) {
      attrs.id = createId();
    }
    if (!attrs.href) {
      attrs.href = '#';
    }

    let content;
    // see if there was any selected content
    let { empty, $cursor, ranges } = state.selection;

    // we'll need to replace the selection before adding the mark
    if (text && text.length > 0) {
      content = state.schema.text(text, [state.schema.marks.anchor.create(attrs)]);
    } else if (empty && dispatch) {
      // we don't insert a hyperlink on empty content
      return false;
    }

    if ((empty && !$cursor) || !markApplies(state.doc, ranges, markType)) {
      return false;
    }

    if (dispatch) {
      const tr = state.tr;
      if (content) {
        // we just replace the selection with a new text
        const from = tr.selection.from;
        tr.replaceSelectionWith(content);
        tr.setSelection(TextSelection.create(tr.doc, from, from + content.nodeSize));
        $cursor = null;
        ranges = tr.selection.ranges;
        empty = false;
      }

      if ($cursor) {
        if (markType && markType.isInSet(state.storedMarks || $cursor.marks())) {
          dispatch(tr.removeStoredMark(markType));
        } else {
          dispatch(tr.addStoredMark(markType.create(attrs)));
        }
      } else {
        let has = false;
        for (let i = 0; !has && i < ranges.length; i++) {
          let { $from, $to } = ranges[i];
          has = tr.doc.rangeHasMark($from.pos, $to.pos, markType);
        }
        for (let i = 0; i < ranges.length; i++) {
          let { $from, $to } = ranges[i];
          if (has) {
            tr.removeMark($from.pos, $to.pos, markType);
          } else {
            tr.addMark($from.pos, $to.pos, markType.create(attrs));
          }
        }
      }
      tr.scrollIntoView();
      dispatch(tr);
    }
    return true;
  };
};

export const updateAndFocusHyperlink = (id, attrs, text: string) => {
  return (state, dispatch) => {
    const tr = state.tr;
    const doc = tr.doc;
    let from, to;

    // resolve the first position where the mark appears
    doc.nodesBetween(0, doc.nodeSize - 2, (n, p) => {
      if (n.marks && n.marks.find((m) => m.attrs.id === id)) {
        if (!from) {
          from = p;
        }
        to = p + n.nodeSize;
      }
    });

    if (!dispatch) {
      return from != null;
    }

    // const { from, to } = findMarkRange(resolvedPos, state.schema.marks.anchor);

    tr.replaceWith(from, to, state.schema.text(text, [state.schema.marks.anchor.create(attrs)]));
    tr.scrollIntoView();
    dispatch(tr);

    return true;
  };
};

export const removeHyperlink = (id: string) => {
  return (state, dispatch) => {
    const tr = state.tr;
    const doc = tr.doc;
    let node;
    let pos;

    doc.nodesBetween(0, doc.nodeSize - 2, (n, p) => {
      if (n.marks && n.marks.find((m) => m.attrs.id === id)) {
        node = n;
        pos = p;
      }
    });

    if (node == null) {
      return false;
    } else if (!dispatch) {
      return true;
    }

    tr.removeMark(pos, pos + node.nodeSize, state.schema.marks.anchor);

    if (dispatch) {
      return dispatch(tr);
    }
  };
};

export const insertTable = (opts = { cols: 3, rows: 3, hasHeaderRow: true }) => {
  return (state, dispatch) => {
    const schema = state.schema;

    const id = createId();
    const attrs = {
      id,
    };

    if (insertPoint(state.doc, state.selection.from, schema.nodes['table']) == null) {
      return false;
    }

    function createRow(cols: number, isHeader = false) {
      const c = [] as any;
      for (let i = 0; i < cols; i++) {
        c.push(
          schema.node(isHeader ? 'table_header' : 'table_cell', {}, [
            schema.node('paragraph', {}, []),
          ]),
        );
      }
      return schema.node('table_row', {}, c);
    }

    const rows = [] as any;
    for (let i = 0; i < opts.rows; i++) {
      rows.push(createRow(opts.cols, i === 0 && opts.hasHeaderRow));
    }

    // keep the table out of list items; decided before dispatch so a menu query agrees with the run
    const result = insertBlockOutsideList(state, schema.nodes['table'], schema.node('table', attrs, rows));
    if (!result) {
      return false;
    }

    if (dispatch) {
      dispatch(result.tr);
    }
    return true;
  };
};

export const insertCaption = (tablenode, pos) => {
  return (state, dispatch) => {
    const schema = state.schema;
    const tr = state.tr;
    const start = pos;
    const end = pos + tablenode.nodeSize;

    let insertAt;
    tr.doc.descendants((node, pos) => {
      if (pos > start && pos < end) {
        if (node.type.name === 'caption') {
          insertAt = insertPoint(tr.doc, pos + 1, schema.nodes.paragraph);
        }
      }
    });

    if (insertAt) {
      tr.insert(insertAt, schema.nodes.paragraph.create({}, []));
      tr.setSelection(TextSelection.create(tr.doc, insertAt + 1));
    }

    if (dispatch) {
      tr.scrollIntoView();
      dispatch(tr);
    }

    return true;
  };
};

export const insertFigure = (type = 'figure', id: string = createId(), attributes: any = {}) => {
  return (state, dispatch) => {
    const attrs = {
      id,
      src: '',
      alt: '',
      decorative: false,
      type,
      ...attributes,
    };

    const figure = state.schema.node('figure', attrs, [
      state.schema.node('caption', {}, [state.schema.node('paragraph')]),
    ]);

    // never place a figure inside a list item
    const result = insertBlockOutsideList(state, state.schema.nodes['figure'], figure);
    if (!result) {
      return false;
    }

    if (dispatch) {
      const { tr, at: insertAt } = result;
      tr.setSelection(NodeSelection.create(tr.doc, insertAt + 2));
      tr.scrollIntoView();
      dispatch(tr);
    }

    return true;
  };
};

export const insertBox = (id: string = createId(), attributes: any = {}) => {
  return (state, dispatch) => {
    const createBox = (state, dispatch) => {
      const caption = state.schema.node(SFNodeType.caption, {}, [
        state.schema.nodes[SFNodeType.paragraph].create({}),
      ]);
      const box = state.schema.node(SFNodeType.figure, { id, type: 'box', ...attributes }, [
        state.schema.nodes[SFNodeType.paragraph].create({}, [state.schema.text('box text')]),
        caption
      ]);

      // keep boxes out of list items
      const result = insertBlockOutsideList(state, state.schema.nodes[SFNodeType.figure], box);
      if (!result) {
        return false;
      }

      if (dispatch) {
        const { tr, at: insertAt } = result;
        tr.setSelection(NodeSelection.create(tr.doc, insertAt + 2));
        tr.scrollIntoView();
        dispatch(tr);
      }

      return true;
    };

    return createBox(state, dispatch);
  };
};
export const insertFootnote = (footnoteContent, id?: string) => {
  return (state, dispatch) => {
    const schema = state.schema;
    const insertAt = insertPoint(state.doc, state.selection.from, schema.nodes['footnote']);

    if (insertAt == null) {
      return false;
    }

    if (!id) {
      id = createId();
    }

    const attrs = {
      id,
      type: 'footnote',
    };

    let content = Fragment.from([schema.text(' ')]);
    // see if there was any selected content
    const { empty, $from, $to, from } = state.selection;
    if (!empty && $from.sameParent($to) && $from.parent.inlineContent) {
      content = $from.parent.content.cut($from.parentOffset, $to.parentOffset);
    }

    if (dispatch) {
      const tr = state.tr.replaceSelectionWith(
        schema.node('footnote', attrs, footnoteContent?.content || content),
      );
      tr.scrollIntoView();
      dispatch(tr);
    }

    return true;
  };
};

export const insertCode = (id: string) => {
  return (state, dispatch) => {
    // a schema without code blocks (the menu asks on every transaction): nothing to insert
    if (!state.schema.nodes[SFNodeType.code]) {
      return false;
    }

    if (!id) {
      id = createId();
    }

    const attrs = {
      id,
      tex: '',
    };

    // code is technically an inline element but should not appear in headings
    const resolvedPos = state.doc.resolve(state.selection.from);
    if (resolvedPos.parent && resolvedPos.parent.type.name === 'heading') {
      return false;
    }

    // keep code blocks out of list items
    const result = insertBlockOutsideList(
      state,
      state.schema.nodes[SFNodeType.code],
      state.schema.node(SFNodeType.code, attrs, [state.schema.text(` `)]),
    );
    if (!result) {
      return false;
    }

    if (dispatch) {
      const { tr } = result;
      tr.scrollIntoView();

      dispatch(tr);
    }

    return true;
  };
};

export const insertMath = (id: string) => {
  return (state, dispatch) => {
    const schema = state.schema;

    if (!id) {
      id = createId();
    }

    const attrs = {
      id,
      tex: '',
    };

    const insertAt = insertPoint(state.doc, state.selection.from, schema.nodes['math']);
    if (insertAt === null) {
      return false;
    }

    if (dispatch) {
      const tr = state.tr;
      tr.insert(insertAt, schema.node('math', attrs, [schema.text(` `)]));
      tr.scrollIntoView();

      dispatch(tr);
    }

    return true;
  };
};

export const insertPageBreak = () => {
  return (state, dispatch) => {
    const schema = state.schema;
    const insertAt = insertPoint(state.doc, state.selection.from, schema.nodes['pageBreak']);

    if (insertAt == null) {
      return false;
    }

    if (dispatch) {
      const tr = state.tr;
      tr.insert(insertAt, schema.node('pageBreak'));
      dispatch(tr);
    }

    return true;
  };
};

export const getStructureCommands = (schema) => {
  const changeToParagraph = setBlockType(schema.nodes.paragraph, {});

  const createHeading = (level: number) => (state, dispatch?) => {
    return setBlockType(schema.nodes.heading, { level })(state, (tr) => {
      if (dispatch) {
        dispatch(tr);
      }
    });
  };

  const heading = (level) => ({
    id: 'h' + level,
    run: (state, dispatch) => {
      return createHeading(level)(state, dispatch);
    },
    select: (state) => {
      return createHeading(level)(state);
    },
  });

  return [
    { id: 'p', run: changeToParagraph, select: changeToParagraph },
    heading(1),
    heading(2),
    heading(3),
    heading(4),
    heading(5),
  ];
};

/**
 * Updates the attributes of a ProseMirror node with the specified ID.
 *
 * This command searches the document for a node with the given `id`. If the node is found,
 * its attributes are updated with the provided `nodeAttrs` object.
 *
 * @param {string} id - The unique identifier of the node to update.
 * @param {object} nodeAttrs - The attributes to update on the node. This object will be merged with the existing node attributes.
 *
 * @example
 * // Update a node with id "node-123" to have new attributes
 * editorView.dispatch(updateNodeAttrs('node-123', { alt: 'New Alt', decorative: true })(editorView.state, editorView.dispatch));
 */
export const updateNodeAttrs = (id: string, nodeAttrs: object) => {
  return (state, dispatch) => {
    try {
      if (!dispatch) {
        return false;
      }

      const tr = state.tr;
      const doc = tr.doc;
      let node;
      let pos;

      doc.nodesBetween(0, doc.nodeSize - 2, (n, p) => {
        if (n.attrs && n.attrs.id === id) {
          node = n;
          pos = p;
          return false;
        }

        return true;
      });

      if (!node) {
        return false;
      }

      tr.setNodeMarkup(pos, null, { ...node.attrs, ...nodeAttrs });

      if (tr.docChanged) {
        tr.scrollIntoView();
        dispatch(tr);
      }

      return true;
    } catch (error) {
      console.error('Error in updateNodeAttrs:', error);
      return false;
    }
  };
};

/**
 * Updates the attributes of a ProseMirror mark across its range.
 *
 * @param {string} markTypeName - The name of the mark type to update.
 * @param {Record<string, any>} markAttrs - New attributes to apply to the mark.
 * @param {{ id?: string | null; from?: number; to?: number }} [options] - Optional identifiers limiting which mark instance to update.
 */
export const updateMarkAttrs = (
  markTypeName: string,
  markAttrs: Record<string, any>,
  options: { id?: string | null; from?: number; to?: number } = {},
) => {
  return (state, dispatch) => {
    const markType = state.schema.marks[markTypeName];
    if (!markType) {
      console.warn(`[updateMarkAttrs] Mark type '${markTypeName}' not found in schema`);
      return false;
    }

    const targetRanges: { from: number; to: number; existingAttrs: Record<string, any> }[] = [];
    const markId = options.id ?? null;
    const rangeStart = typeof options.from === 'number' ? options.from : undefined;
    const rangeEnd = typeof options.to === 'number' ? options.to : undefined;
    const hasExplicitRange =
      typeof rangeStart === 'number' && typeof rangeEnd === 'number' && rangeEnd > rangeStart;

    state.doc.nodesBetween(0, state.doc.content.size, (node, pos) => {
      if (!node.marks || node.marks.length === 0) {
        return true;
      }

      for (const mark of node.marks) {
        if (mark.type !== markType) {
          continue;
        }

        const matchedById =
          markId != null && mark.attrs?.id != null ? mark.attrs.id === markId : markId == null;
        if (!matchedById) {
          continue;
        }

        const from = pos;
        const to = pos + node.nodeSize;
        if (hasExplicitRange && (to <= rangeStart! || from >= rangeEnd!)) {
          continue;
        }

        targetRanges.push({
          from,
          to,
          existingAttrs: { ...mark.attrs },
        });
      }

      return true;
    });

    if (targetRanges.length === 0) {
      return false;
    }

    if (!dispatch) {
      return true;
    }

    const tr = state.tr;
    const mergedAttrs = targetRanges[0].existingAttrs;
    const finalAttrs = { ...mergedAttrs, ...markAttrs };
    if (markId != null && !finalAttrs.id) {
      finalAttrs.id = markId;
    }

    for (const range of targetRanges) {
      tr.removeMark(range.from, range.to, markType);
      tr.addMark(range.from, range.to, markType.create(finalAttrs));
    }

    if (tr.docChanged) {
      tr.scrollIntoView();
      dispatch(tr);
    }

    return true;
  };
};

/**
 * Deletes a ProseMirror node with the specified ID from the document.
 *
 * This command searches the document for a node with the given `id`. If the node is found,
 * it is deleted using ProseMirror's `tr.delete()` method.
 *
 * @param {string} id - The unique identifier of the node to delete.
 */
export const deleteNodeById = (id: string) => {
  return (state, dispatch) => {
    try {
      const tr = state.tr;
      const doc = tr.doc;
      let pos: number | undefined;
      let nodeSize: number | undefined;

      doc.nodesBetween(0, doc.nodeSize - 2, (node, position) => {
        if (node.attrs && node.attrs.id === id) {
          pos = position;
          nodeSize = node.nodeSize;
        }
      });

      if (!pos || !nodeSize) {
        return false;
      }

      if (!dispatch) {
        return true;
      }

      tr.delete(pos, pos + nodeSize);

      if (tr.docChanged) {
        tr.scrollIntoView();
        dispatch(tr);
      }

      return true;
    } catch (error) {
      console.error('Error in deleteNodeById:', error);
      return false;
    }
  };
};

/*** Increases or decreases text size in free form parts (p, h1, h2, h3) */
export const changeTextSize = (increase = true) => {
  return (state, dispatch) => {
    const decrease = !increase;
    const tr = state.tr;

    const parent = state.selection.$anchor.node(-1);
    if (!(parent && parent.type.name === SFNodeType.part && parent.attrs.type === 'free')) {
      // we only allow text size changes in free parts
      return false;
    }

    try {
      if (
        state.selection &&
        state.selection.$anchor.parent &&
        state.selection.$anchor.parent.type.name === 'paragraph'
      ) {
        const para = state.selection.$anchor.parent;
        if (increase && para.attrs?.class?.includes('h1')) {
          return false;
        }

        if (decrease && para.attrs?.class?.includes('small')) {
          return false;
        }

        const increaseSize = (current): string => {
          // small -> '', '' -> h2, h2 -> h1
          if (!current) {
            return 'h2';
          }
          const classes = current.split(' ');
          if (classes.includes('small')) {
            return [...classes.filter((c) => c !== 'small')].join(' ');
          }
          if (classes.includes('h2')) {
            return [...classes.filter((c) => c !== 'h2'), 'h1'].join(' ');
          }
          return [...classes, 'h1'].join(' ');
        };

        const decreaseSize = (current): string => {
          // h1 -> h2, h2 -> '', '' -> small
          if (!current) {
            return 'small';
          }
          const classes = current.split(' ');
          if (classes.includes('h1')) {
            return [...classes.filter((c) => c !== 'h1'), 'h2'].join(' ');
          }
          if (classes.includes('h2')) {
            return [...classes.filter((c) => c !== 'h2' && c !== 'h1')].join(' ');
          }
          if (!classes.includes('small')) {
            return [...classes, 'small'].join(' ');
          }
          return current;
        };

        if (dispatch) {
          let modified = false;
          for (let range of state.selection.ranges) {
            const from = range.$from.pos;
            const to = range.$to.pos;
            // apply the update to all paragraphs
            state.doc.descendants((node, pos) => {
              if (node.type.name === 'paragraph' && pos < from && pos + node.nodeSize > to) {
                let classString = '';
                if (increase) {
                  classString = increaseSize(node.attrs.class);
                } else {
                  classString = decreaseSize(node.attrs.class);
                }
                tr.setNodeMarkup(pos, null, { ...node.attrs, 'class': classString }, node.marks);
                modified = true;
              }
            });
          }
          if (modified) {
            dispatch(tr);
          }
        }

        return true;
      }
    } catch (e: any) {
      console.error(e);
    }
    return false;
  };
};

/** Adds a placeholder element into the document */
export const addPlaceholder = (type: string, label?: string) => {
  return (state, dispatch) => {
    if (!state.schema.nodes.placeHolder) {
      return false;
    }

    const parent = state.selection.$anchor.node(-1);
    if (!(parent && parent.type.name === SFNodeType.part && parent.attrs.type === 'free')) {
      // we only allow placeholders in free parts
      return false;
    }

    const insertAt = insertPoint(state.doc, state.selection.from, state.schema.nodes.placeHolder);
    if (insertAt == undefined) {
      return false;
    }

    const tr = state.tr;
    tr.insert(insertAt, state.schema.nodes.placeHolder.create({ type, label, id: createId() }))

    if (dispatch) {
      dispatch(tr);
      return tr.docChanged;
    }

    return true;
  };
};
